import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiStar } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Container = styled.div`
  padding: 32px;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-top: 8px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #4dd1a0;
    transform: translateY(-2px);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  
  @media (max-width: 768px) {
    margin: 0 -16px;
    border-radius: 0;
    border-left: none;
    border-right: none;
    width: calc(100% + 32px);
  }
`;

const FormContainer = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: ${props => props.show ? 'block' : 'none'};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #4dd1a0;
  }

  &:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s;
  color: #6b7280;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &:first-child {
    margin-right: 8px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const PrimaryBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #d1fae5;
  color: #065f46;
`;

const SetPrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fcd34d;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoPrimaryWarning = styled.div`
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #92400e;
  font-size: 14px;
`;

function AdminBankDetails() {
  const { profile, session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [bankDetails, setBankDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    bank_name: '',
    bank_address: '',
    account_number_iban: '',
    swift_bic_code: '',
    is_primary: false
  });

  useEffect(() => {
    if (session?.user?.id) {
      fetchBankDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const fetchBankDetails = async () => {
    try {
      setLoading(true);
      console.log('=== Fetching Bank Details ===');
      console.log('User ID:', session?.user?.id);
      
      // Get user's brand_id - try brand_managers first, then fallback to brands table
      let brandId = null;
      
      // Try getting from brand_managers table
      const { data: brandManager } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager && brandManager.brand_id) {
        brandId = brandManager.brand_id;
        console.log('Found brand_id from brand_managers:', brandId);
      } else {
        // Fallback: Try to find brand where user is the creator
        const { data: userBrand } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .maybeSingle();

        if (userBrand && userBrand.id) {
          brandId = userBrand.id;
          console.log('Found brand_id from brands table:', brandId);
        }
      }

      if (!brandId) {
        console.warn('⚠️ No brand found for this user');
        setBankDetails([]);
        return;
      }

      console.log('Fetching bank details for brand_id:', brandId);
      
      // Fetch only bank details for this brand
      const { data, error } = await supabase
        .from('bank_details')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      console.log('✅ Found', data?.length || 0, 'bank details');
      setBankDetails(data || []);
    } catch (error) {
      console.error('❌ Error fetching bank details:', error);
      alert(`${isArabic ? 'خطأ في جلب التفاصيل المصرفية:' : 'Error fetching bank details:'} ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      console.log('=== Saving Bank Details ===');
      console.log('User ID:', session.user.id);
      console.log('Form data:', formData);
      
      // Get user's brand_id - try brand_managers first, then fallback to brands table
      let brandId = null;
      
      // Try getting from brand_managers table
      const { data: brandManager, error: brandError } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle 0 or 1 results

      console.log('Brand manager query result:', { brandManager, brandError });

      if (brandManager && brandManager.brand_id) {
        brandId = brandManager.brand_id;
        console.log('Found brand_id from brand_managers:', brandId);
      } else {
        // Fallback: Try to find brand where user is the creator
        console.log('No brand_managers entry found, trying brands table...');
        const { data: userBrand, error: brandTableError } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .maybeSingle();

        console.log('Brands table query result:', { userBrand, brandTableError });

        if (userBrand && userBrand.id) {
          brandId = userBrand.id;
          console.log('Found brand_id from brands table:', brandId);
        } else {
          throw new Error(isArabic ? 'لم يتم العثور على علامة تجارية لهذا المستخدم. يرجى التأكد من أن لديك علامة تجارية مرتبطة بحسابك.' : 'No brand found for this user. Please make sure you have a brand associated with your account.');
        }
      }

      if (!brandId) {
        throw new Error(isArabic ? 'تعذر تحديد معرف العلامة التجارية. يرجى الاتصال بالدعم.' : 'Could not determine brand_id. Please contact support.');
      }

      console.log('Using brand_id:', brandId);

      // If setting this as primary, unset any other primary accounts for this brand
      let hadPreviousPrimary = false;
      if (formData.is_primary) {
        console.log('Setting as primary - checking for existing primary accounts...');
        
        // Find any existing primary accounts for this brand
        const { data: existingPrimary, error: checkError } = await supabase
          .from('bank_details')
          .select('id')
          .eq('brand_id', brandId)
          .eq('is_primary', true)
          .neq('id', editingId || '00000000-0000-0000-0000-000000000000'); // Exclude current record if editing

        if (checkError) {
          console.error('Error checking existing primary:', checkError);
        } else if (existingPrimary && existingPrimary.length > 0) {
          hadPreviousPrimary = true;
          console.log('Found existing primary account(s):', existingPrimary.length);
          console.log('Unsetting previous primary account(s)...');
          
          // Unset all other primary accounts
          const { error: unsetError } = await supabase
            .from('bank_details')
            .update({ is_primary: false })
            .eq('brand_id', brandId)
            .eq('is_primary', true)
            .neq('id', editingId || '00000000-0000-0000-0000-000000000000');

          if (unsetError) {
            console.error('Error unsetting previous primary:', unsetError);
            throw new Error(isArabic ? 'فشل إلغاء تعيين الحساب الرئيسي السابق. يرجى المحاولة مرة أخرى.' : 'Failed to unset previous primary account. Please try again.');
          }
          
          console.log('✅ Previous primary account(s) unset successfully');
        }
      }

      if (editingId) {
        // Update existing bank detail
        console.log('Updating existing bank detail:', editingId);
        const { error } = await supabase
          .from('bank_details')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingId);

        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        console.log('✅ Bank detail updated successfully');
      } else {
        // Check if this is the first bank account - auto-set as primary
        const { data: existingAccounts } = await supabase
          .from('bank_details')
          .select('id')
          .eq('brand_id', brandId);

        const isFirstAccount = !existingAccounts || existingAccounts.length === 0;
        
        // Add new bank detail
        console.log('Inserting new bank detail', isFirstAccount ? '(will be auto-set as primary)' : '');
        const { error } = await supabase
          .from('bank_details')
          .insert({
            ...formData,
            is_primary: isFirstAccount ? true : formData.is_primary, // Auto-set first account as primary
            profile_id: session.user.id,
            brand_id: brandId
          });

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        console.log('✅ Bank detail inserted successfully');
      }

      // Reset form
      setFormData({
        account_holder_name: '',
        bank_name: '',
        bank_address: '',
        account_number_iban: '',
        swift_bic_code: '',
        is_primary: false
      });
      setShowForm(false);
      setEditingId(null);
      
      // Show appropriate success message
      if (hadPreviousPrimary && formData.is_primary) {
        alert(isArabic ? 'تم حفظ التفاصيل المصرفية بنجاح!\n\nملاحظة: تم تغيير حسابك الرئيسي السابق تلقائيًا إلى غير رئيسي لأن كل علامة تجارية يمكن أن يكون لها حساب رئيسي واحد فقط.' : 'Bank details saved successfully!\n\nNote: Your previous primary account has been automatically changed to non-primary since each brand can only have one primary account.');
      } else {
        alert(isArabic ? 'تم حفظ التفاصيل المصرفية بنجاح!' : 'Bank details saved successfully!');
      }
      
      fetchBankDetails();
    } catch (error) {
      console.error('❌ Error saving bank detail:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      alert(`${isArabic ? 'خطأ في حفظ التفاصيل المصرفية:' : 'Error saving bank detail:'} ${error.message}`);
    }
  };

  const handleEdit = (bankDetail) => {
    setFormData({
      account_holder_name: bankDetail.account_holder_name,
      bank_name: bankDetail.bank_name,
      bank_address: bankDetail.bank_address,
      account_number_iban: bankDetail.account_number_iban,
      swift_bic_code: bankDetail.swift_bic_code,
      is_primary: bankDetail.is_primary
    });
    setEditingId(bankDetail.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isArabic ? 'هل أنت متأكد من حذف هذه التفاصيل المصرفية؟' : 'Are you sure you want to delete this bank detail?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bank_details')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchBankDetails();
    } catch (error) {
      console.error('Error deleting bank detail:', error);
      alert(`${isArabic ? 'خطأ في حذف التفاصيل المصرفية:' : 'Error deleting bank detail:'} ${error.message}`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      account_holder_name: '',
      bank_name: '',
      bank_address: '',
      account_number_iban: '',
      swift_bic_code: '',
      is_primary: false
    });
  };

  const handleSetPrimary = async (bankId) => {
    try {
      // Get user's brand_id
      let brandId = null;
      
      const { data: brandManager } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager?.brand_id) {
        brandId = brandManager.brand_id;
      } else {
        const { data: userBrand } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .maybeSingle();
        brandId = userBrand?.id;
      }

      if (!brandId) {
        throw new Error(isArabic ? 'لم يتم العثور على العلامة التجارية' : 'Brand not found');
      }

      // First, unset all primary accounts for this brand
      const { error: unsetError } = await supabase
        .from('bank_details')
        .update({ is_primary: false })
        .eq('brand_id', brandId)
        .eq('is_primary', true);

      if (unsetError) throw unsetError;

      // Then set the selected account as primary
      const { error: setError } = await supabase
        .from('bank_details')
        .update({ is_primary: true })
        .eq('id', bankId);

      if (setError) throw setError;

      alert(isArabic ? 'تم تعيين الحساب الرئيسي بنجاح!' : 'Primary account set successfully!');
      fetchBankDetails();
    } catch (error) {
      console.error('Error setting primary:', error);
      alert(`${isArabic ? 'خطأ:' : 'Error:'} ${error.message}`);
    }
  };

  // Check if there's a primary account
  const hasPrimary = bankDetails.some(bank => bank.is_primary);

  return (
    <Container>
      <Header>
        <div>
          <Title>{isArabic ? 'التفاصيل المصرفية' : 'Bank Details'}</Title>
          <Subtitle>{isArabic ? 'المعلومات المصرفية' : 'Bank Information'}</Subtitle>
        </div>
        {!showForm && (
          <AddButton onClick={() => setShowForm(true)}>
            <FiPlus /> {isArabic ? 'إضافة تفاصيل مصرفية' : 'Add Bank Details'}
          </AddButton>
        )}
      </Header>

      <Card>
        <FormContainer show={showForm}>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label>{isArabic ? 'اسم صاحب الحساب *' : 'Account Holder Name *'}</Label>
                <Input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'اسم البنك *' : 'Bank Name *'}</Label>
                <Input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label>{isArabic ? 'عنوان البنك *' : 'Bank Address *'}</Label>
                <Textarea
                  name="bank_address"
                  value={formData.bank_address}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'رقم الحساب / الآيبان *' : 'Account Number / IBAN *'}</Label>
                <Input
                  type="text"
                  name="account_number_iban"
                  value={formData.account_number_iban}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'رمز SWIFT / BIC' : 'SWIFT / BIC Code'}</Label>
                <Input
                  type="text"
                  name="swift_bic_code"
                  value={formData.swift_bic_code}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    name="is_primary"
                    checked={formData.is_primary}
                    onChange={handleInputChange}
                  />
                  {isArabic ? 'تعيين كحساب مصرفي رئيسي' : 'Set as primary bank account'}
                </CheckboxLabel>
              </FormGroup>
            </FormGrid>

            <ButtonGroup>
              <SaveButton type="submit">
                {editingId ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')} {isArabic ? 'التفاصيل المصرفية' : 'Bank Details'}
              </SaveButton>
              <CancelButton type="button" onClick={handleCancel}>
                {isArabic ? 'إلغاء' : 'Cancel'}
              </CancelButton>
            </ButtonGroup>
          </form>
        </FormContainer>

        {!loading && bankDetails.length > 0 && !hasPrimary && (
          <NoPrimaryWarning>
            <FiStar />
            {isArabic 
              ? 'تحذير: لم يتم تعيين حساب مصرفي رئيسي. يرجى تعيين حساب واحد كرئيسي لاستلام المدفوعات.' 
              : 'Warning: No primary bank account set. Please set one account as primary to receive payments.'}
          </NoPrimaryWarning>
        )}

        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell>{isArabic ? 'صاحب الحساب' : 'Account Holder'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'اسم البنك' : 'Bank Name'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'رقم الحساب' : 'Account Number'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'SWIFT/BIC' : 'SWIFT/BIC'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    {isArabic ? 'جاري التحميل...' : 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : bankDetails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState>
                      <p>{isArabic ? 'لم يتم العثور على تفاصيل مصرفية' : 'No bank details found'}</p>
                      <p>{isArabic ? 'انقر على "إضافة تفاصيل مصرفية" لإضافة حسابك المصرفي الأول' : 'Click "Add Bank Details" to add your first bank account'}</p>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              ) : (
                bankDetails.map(bank => (
                  <TableRow key={bank.id}>
                    <TableCell>{bank.account_holder_name}</TableCell>
                    <TableCell>{bank.bank_name}</TableCell>
                    <TableCell>{bank.account_number_iban}</TableCell>
                    <TableCell>{bank.swift_bic_code || (isArabic ? 'غير متوفر' : 'N/A')}</TableCell>
                    <TableCell>
                      {bank.is_primary ? (
                        <PrimaryBadge>{isArabic ? 'رئيسي' : 'Primary'}</PrimaryBadge>
                      ) : (
                        <SetPrimaryButton onClick={() => handleSetPrimary(bank.id)}>
                          <FiStar size={12} />
                          {isArabic ? 'تعيين كرئيسي' : 'Set Primary'}
                        </SetPrimaryButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <ActionButton onClick={() => handleEdit(bank)} title={isArabic ? 'تعديل' : 'Edit'}>
                        <FiEdit2 />
                      </ActionButton>
                      <ActionButton onClick={() => handleDelete(bank.id)} title={isArabic ? 'حذف' : 'Delete'}>
                        <FiTrash2 />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
}

export default AdminBankDetails;
